import { Suspense } from 'react'
import { FontAwesomeIcon, type FontAwesomeIconProps } from '@fortawesome/react-fontawesome'
import { faBoxTaped } from '@fortawesome/pro-light-svg-icons'
import { type IconName, type IconPrefix } from '@fortawesome/fontawesome-svg-core'

type FaIconStyle = 'solid' | 'regular' | 'light' | 'thin' | 'duotone' | 'brands' | 'sharp-solid'

export type FaIconProps = Omit<FontAwesomeIconProps, 'icon'> & {
    iconName?: string
    iconPrefix?: IconPrefix
    iconStyle?: FaIconStyle
}

const DEFAULT_PREFIX: IconPrefix = 'fal'

const STYLE_PREFIX_MAP: Record<FaIconStyle, IconPrefix> = {
    solid: 'fas',
    regular: 'far',
    light: 'fal',
    thin: 'fat',
    duotone: 'fad',
    brands: 'fab',
    'sharp-solid': 'fass'
}

const PREFIX_ALIASES: Record<string, IconPrefix> = {
    fas: 'fas',
    'fa-solid': 'fas',
    solid: 'fas',
    far: 'far',
    'fa-regular': 'far',
    regular: 'far',
    fal: 'fal',
    'fa-light': 'fal',
    light: 'fal',
    fat: 'fat',
    'fa-thin': 'fat',
    thin: 'fat',
    fad: 'fad',
    'fa-duotone': 'fad',
    duotone: 'fad',
    fab: 'fab',
    'fa-brands': 'fab',
    brands: 'fab',
    fass: 'fass',
    'fa-sharp-solid': 'fass',
    'sharp-solid': 'fass'
}

const ICON_NAME_ALIASES: Record<string, IconName> = {
    measure: 'ruler-combined',
    test: 'xmark'
}

export const parseIconLookup = (
    iconName?: string,
    iconPrefix?: IconPrefix,
    iconStyle?: FaIconStyle
): [IconPrefix, IconName] | null => {
    if (!iconName) return null
    const trimmed = iconName.trim()
    if (!trimmed) return null

    let resolvedPrefix: IconPrefix | undefined
    let nameToken = trimmed

    const spaceParts = trimmed.split(/\s+/)
    if (spaceParts.length > 1) {
        const mappedPrefix = PREFIX_ALIASES[spaceParts[0].toLowerCase()]
        if (mappedPrefix) {
            resolvedPrefix = mappedPrefix
            nameToken = spaceParts[spaceParts.length - 1]
        }
    }

    if (!resolvedPrefix && trimmed.includes(':')) {
        const [maybePrefix, maybeName] = trimmed.split(':')
        const mappedPrefix = PREFIX_ALIASES[maybePrefix.toLowerCase()]
        if (mappedPrefix && maybeName) {
            resolvedPrefix = mappedPrefix
            nameToken = maybeName
        }
    }

    const normalizedName = nameToken.trim().replace(/^fa-/, '').toLowerCase()
    if (!normalizedName) return null

    const prefix = resolvedPrefix ?? iconPrefix ?? (iconStyle ? STYLE_PREFIX_MAP[iconStyle] : DEFAULT_PREFIX)

    return [prefix, ICON_NAME_ALIASES[normalizedName] ?? (normalizedName as IconName)]
}

const FaIcon = ({ iconName, iconPrefix, iconStyle, ...restProps }: FaIconProps) => {
    const iconLookup = parseIconLookup(iconName, iconPrefix, iconStyle)
    if (!iconLookup) return null

    return (
        <Suspense fallback={<FontAwesomeIcon icon={faBoxTaped} />}>
            <FontAwesomeIcon icon={iconLookup} {...restProps} />
        </Suspense>
    )
}

export default FaIcon
